import { ChangeDetectionStrategy, Component, Input, OnInit, Signal, computed, inject } from '@angular/core';

import { Observable, of, distinctUntilChanged, filter, map } from 'rxjs';

import { AspectRatioString, FinnairThemeService } from '@fcom/common';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { CmsArticle, CmsImageData, CmsTaxonomy, CmsTaxonomyGeneral, CmsTeaser } from '@fcom/core-api';
import { isPresent, safeMap } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';

@Component({
  selector: 'cms-text-with-side-image',
  styleUrls: ['./cms-text-with-side-image.component.scss'],
  templateUrl: './cms-text-with-side-image.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsTextWithSideImageComponent implements OnInit {
  private readonly themeService = inject(FinnairThemeService);

  @Input()
  content$: Observable<CmsArticle>;

  @Input()
  imageLeft = false;

  readonly isNewBrand: Signal<boolean> = computed((): boolean => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  cmsTags$: Observable<Array<CmsTaxonomy | string>>;

  aspectRatio$: Observable<AspectRatioString> = of('1x1' as AspectRatioString);
  image$: Observable<CmsImageData>;
  isBlackFriday$: Observable<boolean>;
  link$: Observable<CmsTeaser>;
  noPadding$: Observable<boolean>;
  showSectionMarker$: Observable<boolean>;
  textContent$: Observable<CmsArticle>;

  public readonly TAXONOMY: typeof CmsTaxonomyGeneral = CmsTaxonomyGeneral;

  ngOnInit(): void {
    this.textContent$ = this.content$.pipe(
      filter(isPresent),
      map(({ media: _, ...rest }: CmsArticle): CmsArticle => ({ ...rest, media: [] })),
      finShare()
    );
    this.image$ = this.content$.pipe(
      filter(isPresent),
      map(({ media = [] }: CmsArticle): CmsImageData => media?.[0] as CmsImageData),
      finShare()
    );
    this.link$ = this.content$.pipe(
      filter(isPresent),
      map(({ related = [] }: CmsArticle): CmsTeaser => related?.[0] as CmsTeaser),
      finShare()
    );
    this.cmsTags$ = this.content$.pipe(
      filter(isPresent),
      map((content: CmsArticle): CmsTaxonomy[] =>
        safeMap(content, (c: CmsArticle): CmsTaxonomy[] => c.subjectTaxonomyTags)
      ),
      distinctUntilChanged()
    );
    this.isBlackFriday$ = this.cmsTags$.pipe(map((a: string[]): boolean => a.includes(this.TAXONOMY.BLACK_FRIDAY)));
    this.noPadding$ = this.cmsTags$.pipe(map((a: string[]): boolean => a.includes(this.TAXONOMY.NO_PADDING)));
    this.showSectionMarker$ = this.cmsTags$.pipe(
      map((a: string[]): boolean => !a.includes(this.TAXONOMY.NO_SECTION_MARKER))
    );
  }
}
